import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import InventoryIcon from '@mui/icons-material/Inventory2'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import { UNIT_LABELS, format, marginRates, money, prixTTC, type CurrencyCode } from '@iobiz/shared'
import { useCategories, useItem, useStockLevels, useSuppliers } from '../data/hooks.js'
import { ItemFormDialog } from '../features/catalog/ItemFormDialog.js'
import { Barcode } from '../ui/Barcode.js'
import { VignetteFiche } from '../ui/VignetteFiche.js'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { removeItem } from '../data/repositories/catalog.write.js'
import { StockPanel } from '../features/catalog/StockPanel.js'
import { useSessionStore } from '../stores/sessionStore.js'

/** Fiche article (W-12) — même charpente 360° que la fiche client. */

/**
 * Champ dont la valeur peut être longue — une description, une note.
 *
 * ⭐ Une seule ligne par défaut, dépliable au clic. Une description de vingt
 * lignes repousse hors de l'écran tout ce qui la suit : prix, catégorie,
 * fournisseur. Or on vient sur cette fiche pour ces informations-là, et la
 * description se lit quand on la cherche.
 *
 * Le lien n'apparaît QUE si le texte dépasse : proposer « voir plus » sur une
 * ligne de dix mots apprend à l'utilisateur à ne plus le regarder.
 */
function ChampLong({ label, value }: { label: string; value: string | null }): JSX.Element {
  const [deplie, setDeplie] = useState(false)
  const texte = value?.trim() ?? ''
  // Débordement par la HAUTEUR ou par un saut de ligne : les deux cachent la
  // suite, et seule la seconde se détecte sans mesurer le rendu.
  const longue = texte.length > 90 || texte.includes('\n')

  return (
    <Box sx={{ display: 'flex', py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', width: 180, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-line',
            ...(deplie
              ? {}
              : {
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }),
          }}
        >
          {texte === '' ? '—' : texte}
        </Typography>
        {longue && (
          <Link
            component="button"
            variant="caption"
            underline="hover"
            onClick={() => setDeplie((d) => !d)}
            sx={{ mt: 0.5 }}
          >
            {deplie ? 'Voir moins' : 'Voir plus'}
          </Link>
        )}
      </Box>
    </Box>
  )
}

function Field({ label, value }: { label: string; value: string | number | null }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', width: 180, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2">
        {value === null || value === '' ? '—' : String(value)}
      </Typography>
    </Box>
  )
}

export function ItemDetailPage(): JSX.Element {
  const { slug, id } = useParams<{ slug: string; id: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))

  const item = useItem(store?.storeId, id)
  const categories = useCategories(store?.storeId)
  const suppliers = useSuppliers(store?.storeId, { limit: 200 })?.data ?? []
  // Niveaux lus dans la RÉPLIQUE : le disponible reste affiché hors ligne.
  const niveaux = useStockLevels(store?.storeId)
  const loading = item === undefined
  const [tab, setTab] = useState(0)

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [editing, setEditing] = useState(false)

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!item) {
    return (
      <Alert severity="error" action={<Button onClick={() => navigate(-1)}>Retour</Button>}>
        Article introuvable dans les données de cet appareil.
      </Alert>
    )
  }

  const currency = (item.currencyCode ?? store?.currencyCode ?? 'XAF') as CurrencyCode
  const price = (amount: number): string => format(money(amount, currency), 'fr-FR')
  const taux = marginRates(item.purchasePrice, item.salePrice)
  // Un article n'ayant qu'un visuel hérité reste affichable comme les autres.
  const visuels = item.images?.length ? item.images : item.imageUrl ? [item.imageUrl] : []
  const disponible = niveaux.get(item.id) ?? 0

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigate(`/s/${slug}/catalogue`)}
        >
          Catalogue
        </Link>
        <Typography variant="body2" color="text.primary">
          {item.name}
        </Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 6, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <VignetteFiche
            images={visuels}
            repli={<InventoryIcon sx={{ fontSize: 24, color: 'text.disabled' }} />}
            forme="carree"
          />

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h1">{item.name}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {item.kind === 'service' ? 'Service' : 'Produit'}
              {item.sku && ` · ${item.sku}`}
              {item.categoryName && ` · ${item.categoryName}`}
              {' · Créé en '}
              {new Date(item.createdAt).toLocaleDateString('fr-FR', {
                month: 'long',
                year: 'numeric',
              })}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
              Modifier
            </Button>
            <Button variant="outlined" startIcon={<PointOfSaleIcon />} disabled>
              Vendre
            </Button>
            {/* Même disposition que la fiche client : la suppression vit dans
                un menu, jamais en façade. */}
            <IconButton
              aria-label="Autres actions"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  setConfirming(true)
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon sx={{ fontSize: 18, mr: 2 }} />
                Supprimer
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 4 }}>
        <Tab label="Informations" />
        <Tab label="Stock" />
        <Tab label="Transactions" disabled />
        <Tab label="Documents" disabled />
      </Tabs>

      {tab === 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            alignItems: 'start',
          }}
        >
          <Paper sx={{ p: 6, borderRadius: 3 }}>
            {/* Le prix ouvre la carte : c'est le chiffre qu'on vient chercher
                en premier devant un client, avant toute autre donnée. */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
                bgcolor: 'action.hover',
                borderRadius: 2,
                // ⚠️ AUCUN padding horizontal : le prix doit tomber sur la même
                // verticale que les libellés des champs en dessous. Un `px`
                // ici, si discret soit-il, casse cet alignement — c'est la
                // colonne de gauche qui structure la lecture de la carte.
                px: 0,
                py: 2,
                mb: 4,
              }}
            >
              <Typography className="tabular" sx={{ fontWeight: 700, fontSize: 20 }}>
                {price(item.salePrice)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                HT / {UNIT_LABELS[item.unit]}
              </Typography>
              {taux.markup !== null && <Chip size="small" label={`Marge ${taux.markup} %`} />}
              {!item.trackStock && <Chip size="small" variant="outlined" label="Stock non suivi" />}
            </Box>

            {/* Pas de titre « Général » : la carte s'ouvre sur le prix, et la
                nature des lignes qui suivent se lit d'elle-même. Un intitulé
                fourre-tout n'ajoute rien qu'une ligne à sauter. */}
            <Field label="Type" value={item.kind === 'service' ? 'Service' : 'Produit'} />
            <Field label="Référence" value={item.sku} />
            {/* Le code n'est pas qu'une donnée : c'est une étiquette qu'on
                imprime et qu'on scanne. On le DESSINE plutôt que de l'écrire. */}
            <Box sx={{ display: 'flex', py: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', width: 180, flexShrink: 0 }}
              >
                Code-barres
              </Typography>
              {item.barcode ? (
                <Barcode value={item.barcode} />
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Box>
            <Field label="Catégorie" value={item.categoryName} />
            <Field label="Fournisseur" value={item.supplierName} />
            <Field label="Unité" value={UNIT_LABELS[item.unit]} />
            {/* ⭐ La durée n'apparaît que pour une PRESTATION, et elle dit
                explicitement ce qu'elle commande : sans elle, le service reste
                vendable au comptoir mais l'agenda ne peut pas le proposer,
                faute de savoir quand le rendez-vous finit. La fiche était
                muette là-dessus, alors que la valeur était bien en base. */}
            {item.kind === 'service' && (
              <Field
                label="Durée de la prestation"
                value={
                  item.durationMinutes
                    ? `${item.durationMinutes} minutes`
                    : 'Non renseignée — non proposable à l’agenda'
                }
              />
            )}
            <ChampLong label="Description" value={item.description} />

            <Typography variant="h3" sx={{ mb: 4, mt: 6 }}>
              Tarification
            </Typography>
            <Field
              label={item.kind === 'service' ? 'Coût de revient HT' : "Prix d'achat HT"}
              value={price(item.purchasePrice)}
            />
            <Field label="Prix de vente HT" value={price(item.salePrice)} />
            <Field
              label="TVA"
              value={item.taxRate > 0 ? `${item.taxRate} %` : 'Aucune'}
            />
            <Field
              label="Prix de vente TTC"
              value={price(prixTTC(item.salePrice, item.taxRate))}
            />
            <Field
              label="Marge"
              value={
                taux.markup !== null
                  ? `${price(taux.amount)} · marge ${taux.markup} % · marque ${taux.margin} %`
                  : null
              }
            />
          </Paper>

          <Paper sx={{ p: 6, borderRadius: 3 }}>
            <Typography variant="h3" sx={{ mb: 4 }}>
              Stock
            </Typography>
            {item.trackStock ? (
              <>
                {/* Le disponible AVANT le seuil : le seuil ne veut rien dire
                    sans le chiffre auquel on le compare. */}
                {disponible < 0 ? (
                  // ⭐ Sous zéro, le chiffre EST la mesure de ce que des
                  // commandes ont réservé sans qu'il soit en rayon : cliquer
                  // dessus mène droit à elles, plutôt que de laisser deviner
                  // pourquoi le stock est passé négatif.
                  <Box sx={{ display: 'flex', py: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', width: 180, flexShrink: 0 }}
                    >
                      Stock disponible
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      underline="hover"
                      color="error"
                      sx={{ fontWeight: 700 }}
                      onClick={() =>
                        navigate(`/s/${slug}/ventes?type=order&statut=validated&item=${item.id}`)
                      }
                    >
                      {disponible} — voir les commandes en attente
                    </Link>
                  </Box>
                ) : (
                  <Field label="Stock disponible" value={disponible} />
                )}
                <Field label="Seuil d’alerte" value={item.minStockLevel} />
                {disponible <= item.minStockLevel && (
                  <Chip
                    size="small"
                    color="warning"
                    label="Sous le seuil d’alerte"
                    sx={{ mt: 3 }}
                  />
                )}
                <Button size="small" sx={{ mt: 3 }} onClick={() => setTab(1)}>
                  Voir le stock et ses mouvements
                </Button>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {item.kind === 'service'
                  ? 'Un service ne fait pas l’objet d’un suivi de stock.'
                  : 'Le suivi de stock est désactivé pour cet article.'}
              </Typography>
            )}

            <Typography variant="h3" sx={{ mb: 4, mt: 6 }}>
              Suivi
            </Typography>
            <Field
              label="Créé le"
              value={new Date(item.createdAt).toLocaleDateString('fr-FR')}
            />
            <Field label="Version" value={item.version} />
          </Paper>
        </Box>
      )}

      {tab === 1 && store && <StockPanel storeId={store.storeId} item={item} />}

      <ConfirmDialog
        open={confirming}
        title={`Supprimer « ${item.name} » ?`}
        message="L’article n’apparaîtra plus au catalogue. Les documents qui le mentionnent sont conservés."
        onConfirm={async () => {
          await removeItem(store!.storeId, item)
          navigate(`/s/${slug}/catalogue`)
        }}
        onClose={() => setConfirming(false)}
      />

      {store && (
        <ItemFormDialog
          open={editing}
          storeId={store.storeId}
          item={item}
          categories={categories}
          suppliers={suppliers}
          onClose={() => setEditing(false)}
          onSaved={() => undefined}
        />
      )}
    </Box>
  )
}
